import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { caseKeys } from './use-cases';

import { useApi, useTenant } from '@/context';

// Tenant-scoped, so the tenant is part of the cache key — see caseKeys.
export const trashKeys = {
    all: (tenantId: string) => ['trash', tenantId] as const,
    list: (tenantId: string, query?: { limit?: number; offset?: number }) =>
        [...trashKeys.all(tenantId), 'list', query] as const,
};

export function useTrashedCases(query?: { limit?: number; offset?: number }) {
    const api = useApi();
    const { tenantId } = useTenant();

    return useQuery({
        queryKey: trashKeys.list(tenantId!, query),
        queryFn: () => api.cases.listTrashed(query),
        enabled: !!tenantId,
    });
}

export function useRestoreCase() {
    const api = useApi();
    const queryClient = useQueryClient();
    const { tenantId } = useTenant();

    return useMutation({
        mutationFn: (caseId: string) => api.cases.restore(caseId),
        onSuccess: () => {
            // Invalidate both trash and cases lists
            queryClient.invalidateQueries({ queryKey: trashKeys.all(tenantId!) });
            queryClient.invalidateQueries({ queryKey: caseKeys.all(tenantId!) });
        },
    });
}

export function usePermanentlyDeleteCase() {
    const api = useApi();
    const queryClient = useQueryClient();
    const { tenantId } = useTenant();

    return useMutation({
        mutationFn: (caseId: string) => api.cases.permanentlyDelete(caseId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: trashKeys.all(tenantId!) });
        },
    });
}
